/**
 * The household desk (design A) — the `lg`+ master-detail shell for
 * `/children` and every `/children/:linkId...` sub-route (hub, reports,
 * homework, activity). Below `lg` this renders nothing but `<Outlet />`,
 * i.e. today's pages exactly as they are; from `lg` it lays a list pane
 * (ChildListPane) beside the detail Outlet so the pane persists behind
 * WP2's dialogs/drawer and the in-place view-all lists.
 *
 * The add-child flow (useAddChildFlow) is owned here, not by ChildListPane
 * or the no-children detail separately — two independent instances used to
 * each mount their own LinkChildSheet, so a scan/rail hand-off
 * (location.state.openLinkSheet) opened two dialogs at once.
 * One instance, one sheet, passed down as props.
 */

import { useEffect } from 'react';
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useMinWidth } from '@tupai/shell';
import { useLinksStore } from '@/store/links-store';
import { useCoverageStore } from '@/store/coverage-store';
import ChildListPane from '@/components/child-list-pane';
import NoChildrenEmptyState from '@/components/no-children-empty-state';
import LinkChildSheet from '@/components/link-child-sheet';
import GateNoticeModal from '@/components/gate-notice-modal';
import { useAddChildFlow } from '@/lib/use-add-child-flow';
import { PageSpinner } from '@/components/page-state';
import { SampleFooter } from '@/components/sample-child-ui';
import { isSampleAvailable, isSampleLink } from '@/lib/sample-child';

export const Route = createFileRoute('/_protected/children')({
  component: ChildrenDesk,
});

function NoChildrenDetail({ onAddChild, disabled }: { onAddChild: () => void; disabled: boolean }) {
  // Board A-NoChildren: centred 448px column, 64px of optical lift off the
  // bottom, and the detail's own 24/32 inset so it never touches the pane.
  return (
    <div className="flex-1 flex items-center justify-center px-6 pb-16 xl:pr-8">
      <div className="w-full max-w-md flex flex-col gap-3">
        <NoChildrenEmptyState onAddChild={onAddChild} disabled={disabled} />
      </div>
    </div>
  );
}

function ChildrenDesk() {
  const isLg = useMinWidth('lg');
  const location = useLocation();
  const navigate = useNavigate();
  const { linkId } = useParams({ strict: false });
  const { parentLinks, parentLinksLoaded } = useLinksStore();

  const {
    linkSheetOpen,
    setLinkSheetOpen,
    gateCode,
    setGateCode,
    checkingCapacity,
    openAddChildModal,
  } = useAddChildFlow(isLg);

  // "Linked" and "Covered by a plan" are separate facts (TUP-1835); every
  // row, card and hub band under this layout reads coverage from one store.
  // Refreshed on mount and whenever a child is linked or unlinked, not on
  // every 30s links poll.
  const approvedCount = parentLinks.filter((l) => l.status === 'approved').length;
  useEffect(() => {
    void useCoverageStore.getState().refresh();
  }, [approvedCount]);

  // Below `lg` the desk doesn't exist — this layout is otherwise invisible.
  // The add-child flow still isn't needed here below `lg`: ChildrenPage owns
  // its own instance for that tier, same as before WP1.
  // Every sample-child screen (lib/sample-child.ts) gets the same bar
  // pinned under it: this is a sample, link your own child. Below `lg` the
  // Link sheet lives on ChildrenPage, so Add child goes back there to open it.
  const sampleFooter =
    isSampleLink(linkId) && parentLinksLoaded && isSampleAvailable(parentLinks) ? (
      <SampleFooter
        onAddChild={() =>
          isLg ? openAddChildModal() : navigate({ to: '/children', state: { openLinkSheet: true } })
        }
      />
    ) : null;

  const isRoot = location.pathname === '/children';
  // `/children` at `lg` auto-selects the first approved child — the pane is
  // the entry point now, not a list you land on. Kept as a redirect (not a
  // conditional render) so the URL always names the child actually shown.
  const redirectLinkId =
    isLg && isRoot ? parentLinks.find((l) => l.status === 'approved')?.link_id : undefined;
  useEffect(() => {
    if (redirectLinkId)
      void navigate({ to: '/children/$linkId', params: { linkId: redirectLinkId }, replace: true });
  }, [redirectLinkId, navigate]);

  if (!isLg)
    return (
      <>
        <Outlet />
        {sampleFooter}
      </>
    );

  const linkedChildren = parentLinks.filter((l) => l.status === 'approved');
  const pendingLinkRequests = parentLinks.filter((l) => l.needs_my_action);
  const waitingOnChild = parentLinks.filter((l) => l.status === 'pending' && !l.needs_my_action);
  const hasVisibleLinks =
    linkedChildren.length > 0 || pendingLinkRequests.length > 0 || waitingOnChild.length > 0;

  return (
    // No total to keep in sync anymore: the pane is the only fixed member
    // (304px at `lg`, 352px at `xl`, unchanged) and the detail card absorbs
    // whatever's left, up to the same 1280px ceiling AppShell's own `nav`
    // reading column uses (see columnWidth in AppShell.tsx) — so an
    // ultrawide window doesn't produce an unreadable hub. `ml-4` + the
    // pane's own `pl-4` puts pane content at 32px, level with the header's
    // avatar (TopBar's `px-4` on top of the same `ml-4`). `wide` screens get
    // NO row padding from AppShell (unlike the `lg:pl-4 lg:pr-4 xl:pr-8` a
    // non-`wide` content row gets), so `lg:pr-4 xl:pr-8` here is the desk
    // supplying that same right inset itself — it's what lines the hub
    // card's right edge up with every other module's rail/column edge.
    <div className="flex-1 min-h-0 flex lg:ml-4 lg:pr-4 xl:pr-8 lg:gap-6 xl:gap-8">
      <div className="w-[304px] xl:w-[352px] shrink-0 flex flex-col pt-3 pb-4 pl-4 xl:pt-4 xl:pb-6">
        <ChildListPane
          selectedLinkId={linkId}
          openAddChildModal={openAddChildModal}
          checkingCapacity={checkingCapacity}
        />
      </div>
      {/* The detail is a card, not a pane sharing an edge with the list: the
          gap above separates them (the identity hue already reads from the
          band inside, so the old 2px hue divider was saying it twice), and
          `overflow-hidden` is what rounds the band's own top corners without
          ChildHubPage having to know it sits in a card. `lg:max-w-[1280px]`
          is the ceiling, not the row's — the pane stays fixed-width outside
          it. */}
      <div className="flex-1 min-w-0 lg:max-w-[1280px] flex flex-col min-h-0 mt-3 mb-4 xl:mt-4 xl:mb-6 rounded-lg border border-border bg-surface overflow-hidden">
        {/* `/children` never renders its Outlet here: ChildrenPage is the
            phone/tablet screen, and letting it through would put a second
            title, a second "+" and a second LinkChildSheet/GateNoticeModal
            beside the pane. With nothing approved to select, the detail is
            the empty recipe (no links at all) or simply blank while the
            pane's pending/waiting rows carry the whole story. */}
        {redirectLinkId ? null : !isRoot ? (
          <Outlet />
        ) : !parentLinksLoaded ? (
          // Before links load, `hasVisibleLinks` is false regardless of the
          // real answer — rendering the no-children recipe here flashed it
          // just before the redirect above ever got a chance to fire.
          <PageSpinner />
        ) : hasVisibleLinks ? null : (
          <NoChildrenDetail onAddChild={() => openAddChildModal()} disabled={checkingCapacity} />
        )}
        {sampleFooter}
      </div>

      <LinkChildSheet open={linkSheetOpen} onClose={() => setLinkSheetOpen(false)} />

      {gateCode && <GateNoticeModal code={gateCode} onClose={() => setGateCode(null)} />}
    </div>
  );
}
